import React, { FC, useCallback, useMemo, useState } from 'react';
import i18next from 'i18next';
import { observer } from 'mobx-react';
import { useQuery } from '@apollo/client';
import { DEFAULT_ALERT_FILTER_CATEGORY_TECHNICAL_LIST } from 'core/constants';
import { DataTableStateEvent } from 'primereact/datatable';
import { GqlPlotAlertsPaginated, GqlPlotSensorsSystems } from 'gql/types/plots';
import {
  GET_PLOTS_ALERTS_PAGINATED,
  GET_PLOTS_SENSORS_SYSTEMS,
} from 'gql/queries/plots';
import { AlertSeverity, AlertState, AlertVariant } from 'models/alert';
import { useStores } from 'stores/hooks/hooks';
import { GrowerType } from 'models/grower';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  handleCreateTechnicalData,
  TABLE_ALERTS_MAX_ROWS_NUMBER,
} from '../AlertsTable.utils';
import AlertsTable from '../AlertsTable';
import { useAlertsTableFilters } from '../hooks/useAlertsTableFilters';
import MenuBar from './MenuBar';

const StyledPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StyledAlertsContainer = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

const MenuBarContainer = styled.div`
  padding-left: 1rem;
`;

// eslint-disable-next-line
// @ts-ignore
const defaultLazyParams: DataTableStateEvent = {
  first: 0,
  page: 0,
  rows: TABLE_ALERTS_MAX_ROWS_NUMBER,
  filters: {},
  multiSortMeta: [],
  sortField: '',
  sortOrder: null,
};

const TechnicalAlertsTable: FC = observer(() => {
  const { resellersStore, snackBarStore } = useStores();
  const { t: technicalAlertsT } = useTranslation('liveMonitoring', {
    keyPrefix: 'technicalAlertsTable',
  });

  const [lazyParams, setLazyParams] =
    useState<DataTableStateEvent>(defaultLazyParams);

  const { alertFilters, debouncedFilters, onAlertFilterChange } =
    useAlertsTableFilters();

  const growerIds = resellersStore.selectedTreeNodeCurrentDepthGrowerIds;

  const isGrower = resellersStore.selectedGrowerType === GrowerType.Grower;
  const querySeverity = [
    AlertSeverity.High,
    AlertSeverity.Medium,
    AlertSeverity.Low,
  ];

  const queryUrgency = isGrower
    ? [AlertSeverity.High, AlertSeverity.Medium, AlertSeverity.Low]
    : [AlertSeverity.High];

  const resellerId =
    resellersStore.selectedGrowerType === GrowerType.Reseller
      ? resellersStore.selectedGrowerId
      : null;

  const downloadAllQuery = {
    query: GET_PLOTS_ALERTS_PAGINATED,
    variables: {
      growerIds: [resellersStore.selectedGrowerId],
      alertState: [AlertState.Active],
      severity: querySeverity,
      urgencies: queryUrgency,
      alertFilterCategory: DEFAULT_ALERT_FILTER_CATEGORY_TECHNICAL_LIST,
      subTree: !!resellerId,
    },
    querySensors: GET_PLOTS_SENSORS_SYSTEMS,
    variablesSensors: {
      growerIds,
    },
  };

  const csvFields = useMemo(() => {
    const fields = [];
    if (!isGrower) {
      fields.push({
        fieldKey: 'growerName',
        name: technicalAlertsT('grower_name'),
      });
    }

    fields.push({ fieldKey: 'plotName', name: technicalAlertsT('plot_name') });
    fields.push({
      fieldKey: 'alertType',
      name: technicalAlertsT('alert_description'),
    });

    // fields.push('alertDescription');
    fields.push({
      fieldKey: 'sensorName',
      name: technicalAlertsT('sensor_name'),
    });

    fields.push({
      fieldKey: 'sensorType',
      name: technicalAlertsT('sensor_type'),
    });

    fields.push({
      fieldKey: 'sensorSerial',
      name: technicalAlertsT('sensor_serial'),
    });

    fields.push({
      fieldKey: 'systemSerial',
      name: technicalAlertsT('system_serial'),
    });

    fields.push({
      fieldKey: 'urgency',
      name: technicalAlertsT('alert_urgency'),
    });

    fields.push({
      fieldKey: 'sensorDetails',
      name: technicalAlertsT('alert_details'),
    });

    fields.push({
      fieldKey: 'alertSetTime',
      name: technicalAlertsT('alert_detection_time'),
    });

    return fields;
  }, [isGrower, technicalAlertsT]);

  const { data: plotAlerts, loading: isPlotAlertsLoading } =
    useQuery<GqlPlotAlertsPaginated>(GET_PLOTS_ALERTS_PAGINATED, {
      variables: {
        growerIds: [resellersStore.selectedGrowerId],
        alertState: [AlertState.Active],
        severity: querySeverity,
        urgencies: queryUrgency,
        alertFilterCategory: DEFAULT_ALERT_FILTER_CATEGORY_TECHNICAL_LIST,
        alertPage: lazyParams.page,
        alertSize: lazyParams.rows,
        subTree: !!resellerId,
        ...debouncedFilters,
      },
      fetchPolicy: 'no-cache',
      skip: !growerIds.length,
      onError() {
        snackBarStore.showToast({
          detail: i18next.t('errors:something_went_wrong'),
        });
      },
    });

  const { data: plotsSensorsSystems, loading: isPlotSensorsSystemsLoading } =
    useQuery<GqlPlotSensorsSystems>(GET_PLOTS_SENSORS_SYSTEMS, {
      variables: {
        growerIds,
      },
      fetchPolicy: 'no-cache',
      skip: !growerIds.length,
      onError() {
        snackBarStore.showToast({
          detail: i18next.t('errors:something_went_wrong'),
        });
      },
    });

  const onPageChange = useCallback(
    (params: DataTableStateEvent) => {
      if (params.page !== lazyParams.page) {
        setLazyParams(params);
      }
    },
    [lazyParams],
  );

  const plotAlertsData = useMemo(() => {
    return handleCreateTechnicalData(
      plotAlerts as GqlPlotAlertsPaginated,
      plotsSensorsSystems as GqlPlotSensorsSystems,
    );
  }, [plotAlerts, plotsSensorsSystems]);

  return (
    <StyledPageContainer>
      <MenuBarContainer>
        <MenuBar
          query={downloadAllQuery}
          fields={csvFields}
          tableType="technical"
        />
      </MenuBarContainer>
      <StyledAlertsContainer>
        <AlertsTable
          isReseller={!isGrower}
          alerts={plotAlertsData as GqlPlotAlertsPaginated}
          tableParams={lazyParams}
          loading={isPlotAlertsLoading || isPlotSensorsSystemsLoading}
          onPageChange={onPageChange}
          alertFilters={alertFilters}
          onAlertFilterChange={onAlertFilterChange}
          variant={AlertVariant.Technical}
        />
      </StyledAlertsContainer>
    </StyledPageContainer>
  );
});

export default TechnicalAlertsTable;
